import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, Switch, Tag, Typography } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPost, httpPut } from '../../store/http';
import { NotificationManager } from 'react-notifications';

const { Text } = Typography;

const CommissionCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/commissions/categories`);
            if (response?.success) {
                setCategories(response.data);
            } else {
                NotificationManager.error(response?.message || 'Failed to fetch commission categories.');
            }
        } catch (err) {
            NotificationManager.error('An error occurred while fetching commission categories.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    const showModal = (record = null) => {
        setEditingRecord(record);
        if (record) {
            form.setFieldsValue(record);
        } else {
            form.resetFields();
            form.setFieldsValue({ isActive: true });
        }
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
    };

    const onFinish = async (values) => {
        try {
            let response;
            if (editingRecord) {
                response = await httpPut(`${customBaseUrl.tranv2}/api/v1/admin/commissions/categories/${editingRecord.id}`, values);
            } else {
                response = await httpPost(`${customBaseUrl.tranv2}/api/v1/admin/commissions/categories`, values);
            }

            if (response?.success) {
                NotificationManager.success(`Commission category ${editingRecord ? 'updated' : 'created'} successfully!`);
                handleCancel();
                fetchCategories();
            } else {
                NotificationManager.error(response?.message || `Failed to ${editingRecord ? 'update' : 'create'} commission category.`);
            }
        } catch (err) {
            NotificationManager.error(`An error occurred while ${editingRecord ? 'updating' : 'creating'} commission category.`);
            console.error(err);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Display Name',
            dataIndex: 'displayName',
            key: 'displayName',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (active) => (
                <Tag color={active ? 'success' : 'error'}>
                    {active ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button 
                    type="link" 
                    icon={<EditOutlined />} 
                    onClick={() => showModal(record)}
                >
                    Edit
                </Button>
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography.Title level={4} style={{ margin: 0 }}>Commission Categories</Typography.Title>
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={() => showModal()}
                    style={{ background: '#ff6700', borderColor: '#ff6700' }}
                >
                    Add Category
                </Button>
            </div>

            <Table 
                columns={columns} 
                dataSource={categories} 
                loading={loading}
                rowKey="id"
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={`${editingRecord ? 'Edit' : 'Create'} Commission Category`}
                open={isModalVisible}
                onCancel={handleCancel}
                onOk={() => form.submit()}
                okText={editingRecord ? 'Update' : 'Create'}
                okButtonProps={{ style: { background: '#ff6700', borderColor: '#ff6700' } }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Form.Item
                        name="code"
                        label="Category Code"
                        rules={[{ required: true, message: 'Please input the category code!' }]}
                    >
                        <Input placeholder="e.g. CARD" />
                    </Form.Item>

                    <Form.Item
                        name="displayName"
                        label="Display Name"
                        rules={[{ required: true, message: 'Please input the display name!' }]}
                    >
                        <Input placeholder="e.g. CARD" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'Please input the description!' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Enter category description..." />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label="Active Status"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CommissionCategories;
